import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query'
import { authKeys } from '@/features/auth/api'
import { displayKeys } from '@/features/displays/api'
import { api, unwrap } from '@/lib/api'
import type { Channel, ChannelItem, ChannelLayer, ChannelPreview, DataEnvelope, InsertionPolicy, Paginated, ScopedLayer } from '@/types/api'
import { applyOrder } from './lib/items'

export interface ChannelListParams {
  search?: string
}

export const channelKeys = {
  all: ['channels'] as const,
  lists: () => [...channelKeys.all, 'list'] as const,
  list: (params: ChannelListParams) => [...channelKeys.lists(), params] as const,
  options: () => [...channelKeys.lists(), 'options'] as const,
  details: () => [...channelKeys.all, 'detail'] as const,
  detail: (id: string) => [...channelKeys.details(), id] as const,
  items: (id: string) => [...channelKeys.detail(id), 'items'] as const,
  previews: (id: string) => [...channelKeys.detail(id), 'preview'] as const,
  preview: (id: string, displayId: string | null) => [...channelKeys.previews(id), displayId ?? 'base'] as const,
}

/* ------------------------------------------------------------------ queries */

export function channelsInfiniteOptions(params: ChannelListParams) {
  return infiniteQueryOptions({
    queryKey: channelKeys.list(params),
    queryFn: ({ pageParam, signal }) => api.get<Paginated<Channel>>('/channels', { signal, query: { search: params.search, cursor: pageParam, per_page: 50 } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
  })
}

export function useChannels(params: ChannelListParams) {
  return useInfiniteQuery(channelsInfiniteOptions(params))
}

/** Up to 100 channels by name — for pickers and id → name lookups. */
export function useChannelOptions(enabled = true) {
  return useQuery({
    queryKey: channelKeys.options(),
    queryFn: ({ signal }) => api.get<Paginated<Channel>>('/channels', { signal, query: { per_page: 100 } }),
    select: (page) => page.data,
    enabled,
    staleTime: 30_000,
  })
}

export function channelOptions(id: string) {
  return queryOptions({
    queryKey: channelKeys.detail(id),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<Channel>>(`/channels/${id}`, { signal })),
  })
}

export function useChannel(id: string) {
  return useQuery(channelOptions(id))
}

export function channelItemsOptions(id: string) {
  return queryOptions({
    queryKey: channelKeys.items(id),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<ChannelItem[]>>(`/channels/${id}/items`, { signal })),
  })
}

export function useChannelItems(id: string) {
  return useQuery(channelItemsOptions(id))
}

/**
 * Items of nested channels (children and grandchildren, max depth 3) — used to name skipped
 * items of nested channels in the preview. Grows as child item lists arrive.
 */
export function useNestedChannelItems(rootItems: ChannelItem[] | undefined) {
  const childIds = new Set<string>()
  for (const i of rootItems ?? []) if (i.child_channel) childIds.add(i.child_channel.id)
  const first = useQueries({ queries: [...childIds].map((id) => ({ ...channelItemsOptions(id), staleTime: 30_000 })) })
  const grandIds = new Set<string>()
  for (const q of first) for (const i of q.data ?? []) if (i.child_channel && !childIds.has(i.child_channel.id)) grandIds.add(i.child_channel.id)
  const second = useQueries({ queries: [...grandIds].map((id) => ({ ...channelItemsOptions(id), staleTime: 30_000 })) })
  return [...first, ...second].flatMap((q) => q.data ?? [])
}

export function useChannelPreview(id: string, displayId: string | null, enabled = true) {
  return useQuery({
    queryKey: channelKeys.preview(id, displayId),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<ChannelPreview>>(`/channels/${id}/preview`, { signal, query: { display_id: displayId } })),
    enabled,
    staleTime: 0,
  })
}

/* ---------------------------------------------------------------- caching */

/** After any item / policy change: the channel's items, every preview, list counts — and parents' previews (nesting). */
export function invalidateChannelContent(qc: QueryClient, id: string) {
  void qc.invalidateQueries({ queryKey: channelKeys.items(id) })
  void qc.invalidateQueries({ queryKey: channelKeys.detail(id), exact: true })
  void qc.invalidateQueries({ queryKey: channelKeys.lists() })
  // Any channel may nest this one: refresh every cached preview.
  void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'channels' && q.queryKey[1] === 'detail' && q.queryKey[3] === 'preview' })
  // Display content hashes / sync versions change server-side.
  void qc.invalidateQueries({ queryKey: displayKeys.details() })
}

function patchChannelCache(qc: QueryClient, channel: Channel) {
  qc.setQueryData(channelKeys.detail(channel.id), channel)
  qc.setQueriesData<InfiniteData<Paginated<Channel>>>({ queryKey: channelKeys.lists() }, (data) =>
    data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.map((c) => (c.id === channel.id ? channel : c)) })) } : data,
  )
}

/* ---------------------------------------------------------------- mutations */

export interface ChannelInput {
  name: string
  description?: string | null
}

export function useCreateChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: ChannelInput) => unwrap(api.post<DataEnvelope<Channel>>('/channels', input)),
    onSuccess: (c) => {
      qc.setQueryData(channelKeys.detail(c.id), c)
      void qc.invalidateQueries({ queryKey: channelKeys.lists() })
    },
  })
}

export function useUpdateChannel(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<ChannelInput>) => unwrap(api.patch<DataEnvelope<Channel>>(`/channels/${id}`, input)),
    onSuccess: (c) => {
      patchChannelCache(qc, c)
      void qc.invalidateQueries({ queryKey: channelKeys.lists() })
      // Nested item labels embed the child's name.
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'channels' && q.queryKey[3] === 'items' })
    },
  })
}

export function useDeleteChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/channels/${id}`),
    onSuccess: (_d, id) => {
      qc.setQueriesData<InfiniteData<Paginated<Channel>>>({ queryKey: channelKeys.lists() }, (data) =>
        data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.filter((c) => c.id !== id) })) } : data,
      )
      qc.removeQueries({ queryKey: channelKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: channelKeys.lists() })
    },
  })
}

export interface AddItemInput {
  item_type: 'slideshow' | 'channel'
  slideshow_id?: string
  child_channel_id?: string
  slideshow_version_id?: string | null
  duration_override_ms?: number | null
  layer: ChannelLayer
  scope_id?: string
  valid_from?: string | null
  valid_until?: string | null
  after_item_id?: string
  condition_rule_id?: string | null
  condition_unknown_as?: boolean
}

export function useAddChannelItem(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AddItemInput) => unwrap(api.post<DataEnvelope<ChannelItem>>(`/channels/${channelId}/items`, input)),
    onSuccess: (item) => {
      qc.setQueryData<ChannelItem[]>(channelKeys.items(channelId), (items) => (items ? [...items.filter((i) => i.id !== item.id), item] : items))
      invalidateChannelContent(qc, channelId)
      void qc.invalidateQueries({ queryKey: ['onboarding'] })
    },
  })
}

export interface UpdateItemInput {
  slideshow_version_id?: string | null
  duration_override_ms?: number | null
  valid_from?: string | null
  valid_until?: string | null
  /** Signetix Logic: library rule that must hold (null = unconditional). */
  condition_rule_id?: string | null
  condition_unknown_as?: boolean
}

export function useUpdateChannelItem(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, ...input }: UpdateItemInput & { itemId: string }) => unwrap(api.patch<DataEnvelope<ChannelItem>>(`/channels/${channelId}/items/${itemId}`, input)),
    onSuccess: (item) => {
      qc.setQueryData<ChannelItem[]>(channelKeys.items(channelId), (items) => items?.map((i) => (i.id === item.id ? item : i)))
      invalidateChannelContent(qc, channelId)
    },
  })
}

export function useRemoveChannelItem(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (itemId: string) => api.delete(`/channels/${channelId}/items/${itemId}`),
    onMutate: async (itemId) => {
      await qc.cancelQueries({ queryKey: channelKeys.items(channelId) })
      const snapshot = qc.getQueryData<ChannelItem[]>(channelKeys.items(channelId))
      qc.setQueryData<ChannelItem[]>(channelKeys.items(channelId), (items) => items?.filter((i) => i.id !== itemId))
      return { snapshot }
    },
    onError: (_e, _v, ctx) => ctx?.snapshot && qc.setQueryData(channelKeys.items(channelId), ctx.snapshot),
    onSettled: () => invalidateChannelContent(qc, channelId),
  })
}

/** Optimistic reorder of one block (full order of that layer + scope). */
export function useReorderChannelItems(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (itemIds: string[]) => unwrap(api.post<DataEnvelope<ChannelItem[]>>(`/channels/${channelId}/items/reorder`, { item_ids: itemIds })),
    onMutate: async (itemIds) => {
      await qc.cancelQueries({ queryKey: channelKeys.items(channelId) })
      const snapshot = qc.getQueryData<ChannelItem[]>(channelKeys.items(channelId))
      qc.setQueryData<ChannelItem[]>(channelKeys.items(channelId), (items) => (items ? applyOrder(items, itemIds) : items))
      return { snapshot }
    },
    onError: (_e, _v, ctx) => ctx?.snapshot && qc.setQueryData(channelKeys.items(channelId), ctx.snapshot),
    onSettled: () => invalidateChannelContent(qc, channelId),
  })
}

export interface BlockPolicyInput {
  layer: ScopedLayer
  scope_id: string
  insertion: InsertionPolicy
  interleave_every?: number | null
}

export function useSetBlockPolicy(channelId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: BlockPolicyInput) => api.put<unknown>(`/channels/${channelId}/blocks`, input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: channelKeys.items(channelId) })
      const snapshot = qc.getQueryData<ChannelItem[]>(channelKeys.items(channelId))
      qc.setQueryData<ChannelItem[]>(channelKeys.items(channelId), (items) =>
        items?.map((i) =>
          i.layer === input.layer && i.scope?.id === input.scope_id
            ? { ...i, insertion: input.insertion, interleave_every: input.insertion === 'interleave' ? (input.interleave_every ?? 1) : null }
            : i,
        ),
      )
      return { snapshot }
    },
    onError: (_e, _v, ctx) => ctx?.snapshot && qc.setQueryData(channelKeys.items(channelId), ctx.snapshot),
    onSettled: () => invalidateChannelContent(qc, channelId),
  })
}

/** After default / fallback assignment changes. */
export function invalidateAssignments(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ['onboarding'] })
  void qc.invalidateQueries({ queryKey: authKeys.me() })
}
